import { Button, Tab } from '@features/ui';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { UserDetails, UserRoles } from '@features/admin/settings';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  createUser,
  getUserDetail,
  updateUser,
  updateUserRole,
} from '@src/services/settings.ts';
import { toast } from 'react-toastify';
import { USER_DUPLICATE_EMAIL_OR_NICKNAME } from '@src/constants';
import { useTranslation } from 'react-i18next';
import { useGetMe } from '@src/hooks/useGetMe';
import { canAccess } from '@src/utils/auth';
import { UserRole, UserRolePermission } from '@src/types/settings';

// Schema for User Details tab
const userDetailsSchema = yup.object({
  username: yup
    .string()
    .required('User name is required')
    .matches(/^[^\s]+$/, 'Must not contain spaces'),
  password: yup
    .string()
    .optional()
    .matches(/^(?=.*[a-z])/, 'Must contain at least one lowercase letter')
    .matches(/^(?=.*[A-Z])/, 'Must contain at least one uppercase letter')
    .matches(/^(?=.*\d)/, 'Must contain at least one number')
    .matches(
      /^(?=.*[@$!%*?&^#()\-+=_{}[\]:;<>.,/\\|`~])/,
      'Must contain at least one special character'
    )
    .matches(/^[^\s]+$/, 'Must not contain spaces')
    .when({
      is: (val: string) => !!val,
      then: (schema) =>
        schema
          .min(8, 'Password must be at least 8 characters long')
          .max(16, 'Password must be at most 16 characters long'),
      otherwise: (schema) => schema,
    })
    .nullable(),
  fullname: yup.string().required('Full name is required'),
  email: yup.string().required('Email is required').email('Invalid email'),
  account_active: yup.boolean().nullable(),
  notes: yup.string().optional().nullable(),
});

// Schema for User Roles tab
const userRolesSchema = yup.object({
  role: yup.string().required('Role is required'),
  permissions: yup.array().optional().nullable(),
  monthly_reset_allowed: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value
    )
    .optional()
    .nullable(),
  monthly_new_accounts_allowed: yup
    .number()
    .optional()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value
    )
    .nullable(),
});

export type UserDetailsFormData = yup.InferType<typeof userDetailsSchema>;
export type UserRolesFormData = yup.InferType<typeof userRolesSchema>;

// ---- Tab: User Details Form ----
const UserDetailsTab: FC<{
  method: any;
}> = ({ method }) => {
  return (
    <form autoComplete="off" id="user-details-form">
      <UserDetails method={method} />
    </form>
  );
};

// ---- Tab: User Roles Form ----
const UserRolesTab: FC<{
  method: any;
}> = ({ method }) => {
  return (
    <form autoComplete="off" id="user-roles-form">
      <UserRoles method={method} />
    </form>
  );
};

// ---- Main Component ----
interface SettingEditProps {
  isCreate?: boolean;
}

export const SettingEdits: FC<SettingEditProps> = ({ isCreate }) => {
  const { me, refreshMe } = useGetMe();
  const [tabActive, setTabActive] = useState(0);

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const userDetailsSchemaForMode = useMemo(() => {
    if (!isCreate) return userDetailsSchema;

    return userDetailsSchema.shape({
      password: yup
        .string()
        .required('Password is required')
        .concat(userDetailsSchema.fields.password as yup.StringSchema),
    });
  }, [isCreate]);

  const detailsMethod = useForm<UserDetailsFormData>({
    resolver: yupResolver(userDetailsSchemaForMode),
    defaultValues: { password: '' },
  });

  const rolesMethod = useForm<UserRolesFormData>({
    resolver: yupResolver(userRolesSchema),
    defaultValues: {
      role: UserRole.ADMIN,
      permissions: [],
    },
  });

  const {
    reset: resetDetails,
    watch: watchDetails,
    setValue: setDetailsValue,
  } = detailsMethod;

  const { reset: resetRoles } = rolesMethod;

  const passwordWatch = watchDetails('password');

  useEffect(() => {
    // In edit mode, an empty password means "no change".
    // Convert back to `undefined` so Yup treats it as "not provided".
    // In create mode, password is required, so we must NOT normalize "".
    if (!isCreate && passwordWatch === '') {
      setDetailsValue('password', undefined, {
        shouldValidate: false,
        shouldDirty: true,
        shouldTouch: false,
      });
    }
  }, [isCreate, passwordWatch, setDetailsValue]);

  useEffect(() => {
    if (!id || isCreate) return;

    getUserDetail(id).then((res) => {
      const { internal_notes, role, permissions, ...userData } = res.data;

      resetDetails({
        username: userData.username,
        password: undefined,
        fullname: userData.fullname,
        email: userData.email,
        account_active: userData.account_active,
        notes: internal_notes ?? undefined,
      });

      resetRoles({
        role: role ?? UserRole.ADMIN,
        permissions: permissions ?? [],
        monthly_reset_allowed: userData.monthly_reset_allowed,
        monthly_new_accounts_allowed: userData.monthly_new_accounts_allowed,
      });
    });
  }, [id, isCreate, resetDetails, resetRoles]);

  const handleSuccess = useCallback(async () => {
    await refreshMe();
    navigate('/admin/settings');
  }, [refreshMe, navigate]);

  const handleSave = useCallback(async () => {
    const [detailsValid, rolesValid] = await Promise.all([
      detailsMethod.trigger(),
      rolesMethod.trigger(),
    ]);

    if (!detailsValid || !rolesValid) return;

    const detailsValues = detailsMethod.getValues();
    const rolesValues = rolesMethod.getValues();

    try {
      if (isCreate) {
        await createUser({
          ...detailsValues,
          internal_notes: detailsValues.notes,
          role: rolesValues.role,
          permissions: rolesValues.permissions,
          monthly_reset_allowed: rolesValues.monthly_reset_allowed,
          monthly_new_accounts_allowed:
            rolesValues.monthly_new_accounts_allowed,
        });

        toast.success('User created successfully');
        await handleSuccess();

        return;
      }

      if (!id) return;

      await updateUser(id, {
        ...detailsValues,
        internal_notes: detailsValues.notes,
      });

      await updateUserRole(id, {
        role: rolesValues.role,
        permissions: rolesValues.permissions,
        monthly_reset_allowed: rolesValues.monthly_reset_allowed,
        monthly_new_accounts_allowed: rolesValues.monthly_new_accounts_allowed,
      });

      toast.success('User updated successfully');
      await handleSuccess();
    } catch (err: any) {
      const message =
        err.response?.data?.message || err.response?.data?.detail || '';

      if (err.response?.data?.error_code === USER_DUPLICATE_EMAIL_OR_NICKNAME) {
        toast.error(t('toast.duplicateEmailOrNickname'));

        return;
      }

      toast.error(message || 'Something went wrong. Please try again later.');
    }
  }, [
    detailsMethod,
    handleSuccess,
    id,
    isCreate,
    rolesMethod,
    t,
    updateUserRole,
  ]);

  const tabs = [
    {
      label: 'User Details',
      formId: 'user-details-form',
      component: <UserDetailsTab method={detailsMethod} />,
      disabled: !canAccess(UserRolePermission.MANAGE_USERS, me?.permissions),
    },
    {
      label: 'User Roles',
      formId: 'user-roles-form',
      component: <UserRolesTab method={rolesMethod} />,
      disabled: !canAccess(UserRolePermission.MANAGE_ROLES, me?.permissions),
    },
  ];

  const filteredTabs = tabs.filter((tab) => !tab.disabled);

  return (
    <div className={'flex flex-col relative'}>
      <h1 className={'text-[23px] mb-4'}>Users</h1>
      <Tab
        className={'mb-7'}
        keepMounted={isCreate}
        tabActive={tabActive}
        tabs={filteredTabs}
        onChangeTab={setTabActive}
      />
      <Button
        className={'absolute top-0 right-8'}
        size={'sm'}
        type={'button'}
        variant={'secondary'}
        onClick={handleSave}
      >
        Save
      </Button>
    </div>
  );
};
